/**
 * Module dependencies.
 */

import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'app/components/ui/dialog';
import { Title } from 'app/components/atoms/title/title';
import { Field } from 'app/components/organisms/field/field';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createAppsCryptoTransactionsTemplate, formatNumber } from '../transactions/form-config';
import { SubTitle } from 'app/components/atoms/sub-title/sub-title';
import { Button } from 'app/components/atoms/button/button';
import { useNavigate } from 'react-router-dom';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import moment from 'moment';
import { useGetTransactions } from 'app/hooks/requests/apps-crypto/use-get-transactions';
import BigNumber from 'bignumber.js';
import { useCrudEdit } from 'app/hooks/requests/crud/use-crud-edit';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  item: any;
  setItem: (item: any) => void;
  refetch: () => any;
};

/**
 * Export `NeedUpdateModalComponent` component.
 */

export function NeedUpdateModalComponent({ setItem, item, refetch: overviewRefetch }: Props): JSX.Element {
  const navigate = useNavigate();
  const [translate] = useTranslation();
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const transactionsTemplate = useMemo(() => {
    return createAppsCryptoTransactionsTemplate(translate);
  }, [translate]);
  const { mutate: edit } = useCrudEdit(transactionsTemplate, { id: selectedTransaction?.id });

  const { data: rawTransactions, refetch } = useGetTransactions(undefined, item?.coin ?? '-');
  const transactions = useMemo(() => {
    return (
      rawTransactions?.filter(
        (obj: any) => obj.closedBy.length === 0 && obj.linked.length === 0 && obj.provider.id === item.provider.id
      ) ?? []
    );
  }, [item.provider.id, rawTransactions]);

  const createNewTransaction = useCallback(() => {
    navigate(
      resolveAppPath(
        '/dashboard/apps-crypto/transactions/add',
        {},
        {
          filters: {
            isAirdrop: true,
            providerId: item.provider.id,
            amount: item.sync.diff,
            coin: item.coin,
            createdAt: moment().toISOString()
          }
        }
      )
    );
  }, [item?.coin, item?.provider?.id, item?.sync?.diff, navigate]);

  const handleChange = useCallback(async () => {
    console.log(selectedTransaction, 'selectedTransaction');

    const amount = new BigNumber(selectedTransaction.amount).plus(item.sync.diff).toFixed(8).toString();
    const total = new BigNumber(selectedTransaction.total);
    const unitPrice = total.dividedBy(amount).toFixed(8).toString();

    await edit({
      amount: amount.toString(),
      total: total.toString(),
      unitPrice: unitPrice.toString()
    });

    overviewRefetch();

    setItem(null);
  }, [edit, item.sync, overviewRefetch, selectedTransaction, setItem]);

  useEffect(() => {
    if (item?.coin) {
      refetch();
    }
  }, [item?.coin, refetch]);

  return (
    <Dialog
      onOpenChange={open => {
        if (!open) {
          setItem(null);
        }
      }}
      open={!!item}
    >
      <DialogContent className={'max-h-[85vh] w-full max-w-[1400px] overflow-y-auto'}>
        <DialogTitle className={'sr-only'}>{`Fix ${item.coin} Extra Amount`}</DialogTitle>

        <Title>{`Fix ${item.coin} Extra Amount`}</Title>

        <Field title={'App amount'} value={item.sync.appAmount} />

        <Field title={'Provider amount'} value={item.sync.providerAmount} />

        <Field danger title={'Diff'} value={item.sync.diff} />

        <Field danger title={'Diff USD'} value={formatNumber(item.sync.diffUsd, '$') as string} />

        <SubTitle className={'mt-8 text-[#333]'}>{'Fix with transaction:'}</SubTitle>

        <table className={'w-full'}>
          <thead>
            <tr>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}></th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'ID'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Date'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Amount'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Amount Rest'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Profit Value'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Profit Percent'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Unit Price'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Type'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Airdrop'}</th>
            </tr>
          </thead>

          <tbody>
            {transactions.map((transaction: any) => (
              <tr key={transaction.id}>
                <td className={'border-b border-[#ddd] p-2'}>
                  <input
                    name={'opcao'}
                    onChange={() => {
                      setSelectedTransaction(transaction);
                    }}
                    type={'radio'}
                    value={transaction.id}
                  />
                </td>
                <td className={'border-b border-[#ddd] p-2'}>{transaction.id}</td>
                <td className={'border-b border-[#ddd] p-2'}>{moment(transaction.createdAt).format('DD.MM.YYYY')}</td>
                <td className={'border-b border-[#ddd] p-2'}>{transaction.amount}</td>
                <td className={'border-b border-[#ddd] p-2'}>{transaction.amountRest}</td>
                <td className={'border-b border-[#ddd] p-2'}>{formatNumber(transaction.profitValue, '$')}</td>
                <td className={'border-b border-[#ddd] p-2'}>
                  {new BigNumber(transaction.profitPercent).toFixed(2) + '%'}
                </td>
                <td className={'border-b border-[#ddd] p-2'}>{formatNumber(transaction.unitPrice, '$')}</td>
                <td className={'border-b border-[#ddd] p-2'}>{transaction.type}</td>
                <td className={'border-b border-[#ddd] p-2'}>{transaction.isAirdrop === 1 ? 'YES' : 'NO'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <DialogFooter>
          <div className={'flex justify-end gap-2'} key={'actions'}>
            <Button onClick={() => setItem(false)}>{'Cancel'}</Button>

            <Button onClick={createNewTransaction} type={'primary'}>
              {'Create New Transaction'}
            </Button>

            <Button disabled={!selectedTransaction} onClick={handleChange} type={'primary'}>
              {'OK'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Export `NeedUpdateModal` component.
 */

export function NeedUpdateModal({ setItem, item, refetch }: Props): JSX.Element {
  if (!item) {
    return <></>;
  }

  return <NeedUpdateModalComponent item={item} refetch={refetch} setItem={setItem} />;
}
